import { useState, useEffect, useMemo } from 'react';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { DRILL_TAGS, DRILL_CATEGORIES } from '@/lib/drillTags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sparkles, Clock, Plus, Trash2, GripVertical, Save,
  ArrowRightLeft, ChevronDown, ChevronUp, Tag, Clipboard, CalendarPlus,
  Users, AlertCircle
} from 'lucide-react';
import { SchedulePracticeModal } from '@/components/coach-lab/RunPractice';

const SECTION_TYPES = [
  { value: 'warm_up', label: 'Warm-Up', color: 'bg-green-50 border-green-200 text-green-800' },
  { value: 'skill_work', label: 'Skill Work', color: 'bg-[#13314b]/5 border-[#13314b]/25 text-[#13314b]' },
  { value: 'scrimmage', label: 'Scrimmage', color: 'bg-purple-50 border-purple-200 text-purple-800' },
  { value: 'cool_down', label: 'Cool-Down', color: 'bg-slate-50 border-slate-200 text-slate-700' },
];

export default function PracticePlanBuilder({ user, teams, selectedTeamId, onSwitchTab }) {
  const [mode, setMode] = useState(null); // null | 'auto' | 'custom' | 'view'
  const [totalDuration, setTotalDuration] = useState(90);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planName, setPlanName] = useState('');
  const [focusTags, setFocusTags] = useState([]);
  const [gamesAnalyzed, setGamesAnalyzed] = useState([]);
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDrillPicker, setShowDrillPicker] = useState(null); // { sectionIdx }
  const [showSavedPlans, setShowSavedPlans] = useState(true);
  const [planId, setPlanId] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulePlanId, setSchedulePlanId] = useState(null);
  const [scheduleTeamId, setScheduleTeamId] = useState(null);
  const [prioritizeCustom, setPrioritizeCustom] = useState(false);
  const [hasCustomDrills, setHasCustomDrills] = useState(null); // null = unknown
  const [completedGameCount, setCompletedGameCount] = useState(null);
  const [noGamesMessage, setNoGamesMessage] = useState('');

  // The team to use is always the globally selected team
  const teamId = selectedTeamId || '';
  const selectedTeam = teams.find(t => t.team_id === teamId);

  useEffect(() => { fetchSavedPlans(); }, []);

  // Check completed game count + custom drills when team changes
  useEffect(() => {
    if (!teamId) {
      setCompletedGameCount(null);
      setHasCustomDrills(null);
      return;
    }
    const check = async () => {
      try {
        // Check completed games for this team
        const gRes = await api.get(`/teams/${teamId}/games`);
        const completed = (gRes.data || []).filter(g => g.status === 'final' || g.status === 'locked' || g.status === 'manual_entry');
        setCompletedGameCount(completed.length);
      } catch { setCompletedGameCount(0); }
      try {
        // Check if user has custom drills
        const dRes = await api.get('/drills', { params: { visibility: 'private', limit: 1 } });
        setHasCustomDrills((dRes.data?.total || 0) > 0);
      } catch { setHasCustomDrills(false); }
    };
    check();
  }, [teamId]);

  const fetchSavedPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await api.get('/practice-plans');
      setSavedPlans(res.data.plans);
    } catch { /* ignore */ }
    finally { setLoadingPlans(false); }
  };

  const [disclaimer, setDisclaimer] = useState(null);

  const handleGenerate = async () => {
    if (!teamId) { toast.error('Select a team from the top-right selector'); return; }

    // 0 games check — don't call the API
    if (completedGameCount === 0) {
      setNoGamesMessage('Auto-generated practice plans require at least one completed game. Create your first plan manually in Coach\'s Lab.');
      return;
    }

    setGenerating(true);
    setNoGamesMessage('');
    try {
      const res = await api.post('/practice-plans/generate', {
        team_id: teamId,
        total_duration_minutes: totalDuration,
        prioritize_custom_drills: prioritizeCustom,
      });
      setPlan(res.data.sections);
      setFocusTags(res.data.focus_tags);
      setGamesAnalyzed(res.data.games_analyzed);
      setDisclaimer(res.data.disclaimer || null);
      if (res.data.has_custom_drills !== undefined) setHasCustomDrills(res.data.has_custom_drills);
      setPlanName('');
      setPlanId(null);
      setMode('auto');
      toast.success(`Plan generated from ${res.data.games_analyzed.length} game(s)`);
    } catch (e) {
      const detail = apiErrorMessage(e, 'Failed to generate plan');
      if (e.response?.status === 404) {
        setNoGamesMessage(detail);
      } else {
        toast.error(detail);
      }
    }
    finally { setGenerating(false); }
  };

  const handleCustom = () => {
    setPlan(SECTION_TYPES.map(s => ({
      section_type: s.value, section_duration: s.value === 'skill_work' ? 45 : s.value === 'scrimmage' ? 20 : 10,
      drills: [],
    })));
    setFocusTags([]);
    setGamesAnalyzed([]);
    setPlanName(''); // Start with blank name
    setPlanId(null);
    setMode('custom');
  };

  const handleSave = async (asTemplate = false) => {
    if (!planName.trim()) { toast.error('Give your plan a name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: planName, team_id: teamId, total_duration_minutes: totalDuration,
        source: mode === 'auto' ? 'auto' : 'custom', focus_tags: focusTags,
        games_analyzed: gamesAnalyzed, sections: plan, is_template: asTemplate,
      };
      if (planId) {
        await api.put(`/practice-plans/${planId}`, payload);
        toast.success('Plan updated');
      } else {
        const res = await api.post('/practice-plans', payload);
        if (res.data?.plan_id) setPlanId(res.data.plan_id);
        toast.success(asTemplate ? 'Template saved' : 'Plan saved');
      }
      fetchSavedPlans();
    } catch (e) { toast.error(apiErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/practice-plans/${planId}`);
      toast.success('Plan deleted');
      fetchSavedPlans();
    } catch { toast.error('Failed to delete plan'); }
  };

  const loadPlan = (savedPlan) => {
    setPlan(savedPlan.sections);
    setPlanId(savedPlan.plan_id);
    setFocusTags(savedPlan.focus_tags || []);
    setGamesAnalyzed(savedPlan.games_analyzed || []);
    setPlanName(savedPlan.name);
    setTotalDuration(savedPlan.total_duration_minutes || 90);
    setMode('view');
  };

  const removeDrill = (sectionIdx, drillIdx) => {
    setPlan(p => p.map((s, i) => i === sectionIdx ? { ...s, drills: s.drills.filter((_, j) => j !== drillIdx) } : s));
  };

  const updateDrillDuration = (sectionIdx, drillIdx, dur) => {
    setPlan(p => p.map((s, i) => i === sectionIdx ? {
      ...s, drills: s.drills.map((d, j) => j === drillIdx ? { ...d, duration_minutes: Math.max(1, parseInt(dur) || 1) } : d)
    } : s));
  };

  const updateSectionDuration = (sectionIdx, dur) => {
    setPlan(p => p.map((s, i) => i === sectionIdx ? { ...s, section_duration: Math.max(1, parseInt(dur) || 1) } : s));
  };

  const aggregateDuration = () => {
    if (!plan) return 0;
    return plan.reduce((sum, s) => sum + s.drills.reduce((ds, d) => ds + (d.duration_minutes || 0), 0), 0);
  };

  const sectionStyle = (type) => SECTION_TYPES.find(s => s.value === type) || SECTION_TYPES[0];

  // ─── No plan loaded: show launcher ───
  if (!mode) {
    return (
      <div>
        {/* Launcher */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-xl border-2 border-slate-200 p-6 hover:border-[#13314b]/40 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#13314b]/10 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#13314b]" />
              </div>
              <h3 className="font-bold text-slate-900 uppercase text-sm">Auto-Generate</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Build a plan from your team's recent games. We'll match drills to your top practice focus areas.
            </p>

            {/* Show selected team context */}
            {selectedTeam ? (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <Users className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700">{selectedTeam.name}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-amber-700">Select a team from the top-right selector</span>
              </div>
            )}

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 mb-1.5 block">Duration (min)</label>
                <div className="flex rounded-lg overflow-hidden border border-slate-200" data-testid="plan-duration-segmented">
                  {[60, 90, 120].map(d => (
                    <button
                      key={d}
                      onClick={() => setTotalDuration(d)}
                      data-testid={`duration-${d}`}
                      className={`flex-1 py-2 text-sm font-bold transition-colors ${
                        totalDuration === d
                          ? 'bg-slate-900 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      style={{ borderRight: d !== 120 ? '1px solid #e2e8f0' : 'none' }}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prioritize My Drills toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="prioritize-custom"
                  checked={prioritizeCustom}
                  onChange={e => setPrioritizeCustom(e.target.checked)}
                  disabled={!hasCustomDrills}
                  className="w-4 h-4 rounded border-slate-300 text-[#13314b] focus:ring-[#13314b] disabled:opacity-50"
                  data-testid="prioritize-custom-drills-checkbox"
                />
                <label
                  htmlFor="prioritize-custom"
                  className={`text-xs font-semibold uppercase tracking-wide ${hasCustomDrills ? 'text-slate-700 cursor-pointer' : 'text-slate-400 cursor-not-allowed'}`}
                >
                  Prioritize My Drills
                </label>
                {hasCustomDrills === false && (
                  <span className="text-[10px] text-slate-400 italic">Update My Drills to include them in your practice plan</span>
                )}
              </div>
            </div>

            {/* No games alert */}
            {noGamesMessage && (
              <div className="flex items-start gap-2 mb-3 px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-200" data-testid="no-games-alert">
                <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">{noGamesMessage}</p>
              </div>
            )}

            <Button
              className="w-full bg-[#13314b] hover:bg-[#0e2638]"
              onClick={handleGenerate}
              disabled={generating || !teamId || completedGameCount === 0}
              data-testid="generate-plan-btn"
            >
              {generating ? 'Analyzing games...' : completedGameCount === 0 ? 'No Games Available' : 'Generate Plan'}
            </Button>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-6 hover:border-slate-300 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Clipboard className="w-5 h-5 text-slate-600" />
              </div>
              <h3 className="font-bold text-slate-900 uppercase text-sm">Custom Plan</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">Start from scratch. Add sections, pick drills from the library, and set your own time blocks.</p>
            <div className="space-y-3 mb-4">
              {selectedTeam ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-700">{selectedTeam.name}</span>
                  <span className="text-[10px] text-slate-400">(optional)</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-sm text-slate-400">No team selected (optional)</span>
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 mb-1.5 block">Duration (min)</label>
                <div className="flex rounded-lg overflow-hidden border border-slate-200">
                  {[60, 90, 120].map(d => (
                    <button
                      key={d}
                      onClick={() => setTotalDuration(d)}
                      className={`flex-1 py-2 text-sm font-bold transition-colors ${
                        totalDuration === d
                          ? 'bg-slate-900 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      style={{ borderRight: d !== 120 ? '1px solid #e2e8f0' : 'none' }}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={handleCustom} data-testid="custom-plan-btn">
              Create Blank Plan
            </Button>
          </div>
        </div>

        {/* Saved Plans */}
        <div>
          <button className="flex items-center gap-2 mb-3" onClick={() => setShowSavedPlans(p => !p)}>
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Saved Plans</h2>
            {showSavedPlans ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showSavedPlans && (
            loadingPlans ? <p className="text-sm text-slate-400">Loading...</p> :
            savedPlans.length === 0 ? <p className="text-sm text-slate-400">No saved plans yet</p> :
            <div className="space-y-2">
              {savedPlans.map(sp => (
                <div key={sp.plan_id} className="bg-white rounded-lg border border-slate-200 p-3 flex items-center justify-between" data-testid={`saved-plan-${sp.plan_id}`}>
                  <div className="flex-1 cursor-pointer" onClick={() => loadPlan(sp)}>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm text-slate-900">{sp.name}</p>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-bold uppercase">{sp.source}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{sp.total_duration_minutes}min &middot; {new Date(sp.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      type="button"
                      className="h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-slate-100 transition-colors" 
                      title="Schedule Practice"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        e.preventDefault();
                        setSchedulePlanId(sp.plan_id);
                        setScheduleTeamId(sp.team_id || '');
                        setShowScheduleModal(true); 
                      }}
                      data-testid={`schedule-plan-${sp.plan_id}`}
                    >
                      <CalendarPlus className="w-3.5 h-3.5 text-slate-500 hover:text-slate-700" />
                    </button>
                    <button 
                      type="button"
                      className="h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-red-50 transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleDeletePlan(sp.plan_id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schedule Practice Modal (from saved plans list) */}
        <SchedulePracticeModal
          open={showScheduleModal}
          onOpenChange={v => { setShowScheduleModal(v); if (!v) { setSchedulePlanId(null); setScheduleTeamId(null); } }}
          teams={teams || []}
          selectedTeamId={teamId}
          selectedSeasonId={null}
          editData={null}
          preselectedPlanId={schedulePlanId}
          preselectedTeamId={scheduleTeamId}
          onSaved={() => { setShowScheduleModal(false); toast.success('Practice scheduled! Go to Run Practice tab to view.'); }}
          onGoToPlans={() => {}}
        />
      </div>
    );
  }

  // ─── Plan Editor / Viewer ───
  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div className="flex-1">
          <button className="inline-flex items-center justify-center h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-[.12em] mb-2 transition-colors" style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', fontFamily: "'Barlow Condensed', sans-serif" }} onClick={() => setMode(null)} data-testid="back-to-plans">
            &larr; Back to Plans
          </button>
          <Input 
            value={planName} 
            onChange={e => setPlanName(e.target.value)} 
            placeholder="Name Your Practice Plan"
            className="text-lg font-bold border border-slate-200 hover:border-slate-400 focus:border-slate-900 px-2 py-1 h-auto rounded-md uppercase bg-white placeholder:text-slate-400 placeholder:font-normal placeholder:normal-case"
            data-testid="plan-name-input" 
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-semibold uppercase tracking-[.07em] px-5 h-10" style={{ fontFamily: "'Barlow Condensed', sans-serif" }} onClick={() => handleSave(false)} disabled={saving} data-testid="save-plan-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save Plan'}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => {
              if (!planName.trim()) { toast.error('Name your plan before scheduling'); return; }
              if (!planId) { toast.error('Save your plan first before scheduling'); return; }
              setSchedulePlanId(planId);
              setShowScheduleModal(true);
            }}
            disabled={!planId || !planName.trim()}
            data-testid="schedule-from-editor-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-1" /> Schedule
          </Button>
        </div>
      </div>

      {/* Aggregate Duration Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-slate-400" />
          <div>
            <span className="font-bold text-xl text-slate-900">{aggregateDuration()}</span>
            <span className="text-sm text-slate-500 ml-1">min filled</span>
          </div>
          <span className="text-slate-300">/</span>
          <div>
            <span className="font-bold text-lg text-slate-600">{totalDuration}</span>
            <span className="text-sm text-slate-400 ml-1">min target</span>
          </div>
        </div>
        {focusTags.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end">
            {focusTags.map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">{t}</span>
            ))}
          </div>
        )}
      </div>

      {gamesAnalyzed.length > 0 && (
        <div className="text-xs text-slate-400 mb-4">
          Based on: {gamesAnalyzed.map((g, i) => <span key={i}>{i > 0 && ', '}{g.opponent ? `vs ${g.opponent}` : 'Team self-analysis'}</span>)}
        </div>
      )}

      {disclaimer && (
        <div className="px-4 py-2.5 mb-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="practice-plan-disclaimer">
          <p className="text-xs text-amber-700 leading-relaxed">
            <strong>Note:</strong> {disclaimer}
          </p>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {plan?.map((section, sIdx) => {
          const style = sectionStyle(section.section_type);
          return (
            <div key={sIdx} className={`rounded-xl border-2 p-4 ${style.color}`} data-testid={`plan-section-${section.section_type}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold uppercase text-sm">{style.label}</h3>
                <div className="flex items-center gap-2">
                  <Input type="number" min={1} value={section.section_duration}
                    onChange={e => updateSectionDuration(sIdx, e.target.value)}
                    className="w-16 h-7 text-xs text-center bg-white/80" />
                  <span className="text-xs font-medium">min</span>
                </div>
              </div>

              {/* Drills in this section */}
              {section.drills.length === 0 ? (
                <p className="text-xs text-slate-400 italic mb-2">No drills added</p>
              ) : (
                <div className="space-y-1.5 mb-3">
                  {section.drills.map((drill, dIdx) => (
                    <div key={dIdx} className="bg-white/80 rounded-lg p-2.5 flex items-center gap-2 group" data-testid={`plan-drill-${sIdx}-${dIdx}`}>
                      <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{drill.name}</p>
                        {drill.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {drill.tags.map((t, i) => (
                              <span key={i} className="text-[9px] px-1 py-0 bg-amber-50 text-amber-600 rounded">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Input type="number" min={1} value={drill.duration_minutes}
                        onChange={e => updateDrillDuration(sIdx, dIdx, e.target.value)}
                        className="w-14 h-7 text-xs text-center" />
                      <span className="text-[10px] text-slate-400">min</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                        onClick={() => setShowDrillPicker({ sectionIdx: sIdx, replaceDrillIdx: dIdx })}
                        data-testid={`swap-drill-${sIdx}-${dIdx}`}>
                        <ArrowRightLeft className="w-3 h-3 text-[#13314b]" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                        onClick={() => removeDrill(sIdx, dIdx)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowDrillPicker({ sectionIdx: sIdx })} data-testid={`add-drill-section-${sIdx}`}>
                <Plus className="w-3 h-3 mr-1" /> Add Drill
              </Button>
            </div>
          );
        })}
      </div>

      {/* Drill Picker Modal */}
      <DrillPickerModal
        open={!!showDrillPicker}
        focusTags={focusTags}
        sectionType={showDrillPicker ? plan[showDrillPicker.sectionIdx]?.section_type : null}
        user={user}
        onClose={() => setShowDrillPicker(null)}
        onSelect={(drill) => {
          const { sectionIdx, replaceDrillIdx } = showDrillPicker;
          setPlan(p => p.map((s, i) => {
            if (i !== sectionIdx) return s;
            const newDrill = { drill_id: drill.drill_id, name: drill.name, duration_minutes: drill.duration_minutes, tags: drill.tags, description: drill.description, category: drill.category };
            if (replaceDrillIdx != null) {
              return { ...s, drills: s.drills.map((d, j) => j === replaceDrillIdx ? newDrill : d) };
            }
            return { ...s, drills: [...s.drills, newDrill] };
          }));
          setShowDrillPicker(null);
        }}
      />

      {/* Schedule Practice Modal (from saved plans) */}
      <SchedulePracticeModal
        open={showScheduleModal}
        onOpenChange={v => { setShowScheduleModal(v); if (!v) { setSchedulePlanId(null); setScheduleTeamId(null); } }}
        teams={teams || []}
        selectedTeamId={teamId}
        selectedSeasonId={null}
        editData={null}
        preselectedPlanId={schedulePlanId}
        preselectedTeamId={scheduleTeamId || teamId}
        onSaved={() => { setShowScheduleModal(false); toast.success('Practice scheduled! Go to Run Practice tab to view.'); }}
        onGoToPlans={() => {}}
      />
    </div>
  );
}

/* ─── Filter Tag Groups for Drill Picker ─── */
const FILTER_TAG_GROUPS = [
  { label: 'Offense', tags: ['Shooting Efficiency', 'Shot Selection', 'Perimeter & Mid-Range Shooting', 'Three-Point Shooting', 'Finishing at the Rim', 'Attacking the Rim'] },
  { label: 'Defense', tags: ['Rim Protection & Contesting', 'Perimeter Defense', 'Steals & On-Ball Pressure', 'Defensive Discipline'] },
  { label: 'Ball Skills', tags: ['Ball Handling & Decision-Making', 'Turnovers & Ball Security'] },
  { label: 'Rebounding', tags: ['Offensive Rebounding', 'Defensive Rebounding'] },
  { label: 'Free Throws', tags: ['Free-Throw Consistency', 'Drawing Contact & Fouls', 'Foul Discipline'] },
];

/* ─── Multi-Step Drill Picker Modal ─── */
function DrillPickerModal({ open, focusTags, sectionType, user, onClose, onSelect }) {
  const [allDrills, setAllDrills] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newDuration, setNewDuration] = useState(10);
  const [creating, setCreating] = useState(false);
  
  // Stepper state
  const [step, setStep] = useState(1); // 1 = Select Topic, 2 = Select Drill
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedDrill, setSelectedDrill] = useState(null);
  
  // Filter states (for step 2)
  const [difficultyFilters, setDifficultyFilters] = useState([]);
  const [ownershipFilters, setOwnershipFilters] = useState([]);

  // Get section label for display
  const sectionLabel = SECTION_TYPES.find(s => s.value === sectionType)?.label || sectionType;

  // Filter drills by exact category match (strict)
  const categoryDrills = useMemo(() => {
    if (!sectionType) return allDrills;
    return allDrills.filter(d => d.category === sectionType);
  }, [allDrills, sectionType]);

  // Check if there are any drills for this category
  const hasDrillsForCategory = categoryDrills.length > 0;

  // Reset everything when modal opens/closes
  useEffect(() => {
    if (!open) {
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewDuration(10);
      setStep(1);
      setSelectedTopic(null);
      setSelectedDrill(null);
      setDifficultyFilters([]);
      setOwnershipFilters([]);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const [drillsRes, favRes] = await Promise.all([
          api.get('/drills?limit=200'),
          api.get('/drills/favorites')
        ]);
        setAllDrills(drillsRes.data.drills || []);
        setFavorites(favRes.data.favorite_drill_ids || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [open]);

  // Toggle favorite
  const toggleFavorite = async (drillId, e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/drills/${drillId}/favorite`);
      setFavorites(res.data.favorite_drill_ids || []);
    } catch {
      toast.error('Failed to update favorite');
    }
  };

  // Filter drills based on selected topic and filters
  const filteredDrills = useMemo(() => {
    let result = [...categoryDrills];
    
    // 1. Filter by selected topic (required in step 2)
    if (selectedTopic) {
      result = result.filter(d => (d.tags || []).includes(selectedTopic));
    }
    
    // 2. Filter by difficulty
    if (difficultyFilters.length > 0) {
      result = result.filter(d => difficultyFilters.includes(d.difficulty));
    }
    
    // 3. Filter by ownership
    if (ownershipFilters.length > 0) {
      result = result.filter(d => {
        const isMine = d.created_by === user?.user_id;
        const isFavorite = favorites.includes(d.drill_id);
        const isPublic = d.is_public;
        
        if (ownershipFilters.includes('mine') && isMine) return true;
        if (ownershipFilters.includes('favorites') && isFavorite) return true;
        if (ownershipFilters.includes('public') && isPublic && !isMine) return true;
        return false;
      });
    }
    
    // Sort alphabetically
    result.sort((a, b) => a.name.localeCompare(b.name));
    
    return result;
  }, [categoryDrills, selectedTopic, difficultyFilters, ownershipFilters, favorites, user]);

  // Count drills per topic (for step 1 display) - using strict category filter
  const topicCounts = useMemo(() => {
    const counts = {};
    FILTER_TAG_GROUPS.forEach(group => {
      group.tags.forEach(tag => {
        counts[tag] = categoryDrills.filter(d => (d.tags || []).includes(tag)).length;
      });
    });
    return counts;
  }, [categoryDrills]);

  const hasActiveFilters = difficultyFilters.length > 0 || ownershipFilters.length > 0;
  
  const clearFilters = () => {
    setDifficultyFilters([]);
    setOwnershipFilters([]);
  };

  const toggleFilter = (arr, setArr, value) => {
    if (arr.includes(value)) {
      setArr(arr.filter(v => v !== value));
    } else {
      setArr([...arr, value]);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Drill name is required'); return; }
    setCreating(true);
    try {
      const res = await api.post('/drills', {
        name: newName.trim(),
        description: newDesc.trim(),
        duration_minutes: newDuration,
        category: sectionType || 'skill_work',
        difficulty: 'intermediate',
        tags: selectedTopic ? [selectedTopic] : [],
        is_public: false,
      });
      toast.success('Custom drill created');
      onSelect(res.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to create drill'));
    } finally { setCreating(false); }
  };

  const handleAcceptTopic = () => {
    if (selectedTopic) {
      setStep(2);
      setSelectedDrill(null);
    }
  };

  const handleBack = () => {
    setStep(1);
    setSelectedDrill(null);
    clearFilters();
  };

  const handleAddDrill = () => {
    if (selectedDrill) {
      onSelect(selectedDrill);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 [&>button]:border-0 [&>button]:shadow-none">
        {/* Header with Stepper */}
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-slate-100">
          <DialogTitle className="font-bold text-base">
            Pick a {sectionLabel} drill
          </DialogTitle>
          
          {/* Stepper Indicator - only show if drills exist */}
          {!showCreate && hasDrillsForCategory && (
            <div className="flex items-center gap-2 mt-3">
              <div className={`flex items-center gap-1.5 ${step === 1 ? 'text-[#13314b]' : 'text-slate-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 1 ? 'bg-[#13314b] text-white' : 'bg-slate-200 text-slate-500'
                }`}>1</div>
                <span className="text-xs font-semibold">Topic</span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200 mx-1">
                <div className={`h-full bg-[#13314b] transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
              </div>
              <div className={`flex items-center gap-1.5 ${step === 2 ? 'text-[#13314b]' : 'text-slate-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 2 ? 'bg-[#13314b] text-white' : 'bg-slate-200 text-slate-500'
                }`}>2</div>
                <span className="text-xs font-semibold">Drill</span>
              </div>
            </div>
          )}
        </DialogHeader>

        {/* Empty State - No drills for this category */}
        {!loading && !showCreate && !hasDrillsForCategory ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1">
              You don't have any {sectionLabel} drills yet.
            </p>
            <p className="text-xs text-slate-500 mb-6">
              Create a custom drill to get started.
            </p>
            <Button 
              onClick={() => setShowCreate(true)}
              className="bg-[#13314b] hover:bg-[#0e2638] text-white font-bold uppercase tracking-wider text-sm h-11 px-6"
              data-testid="create-drill-empty-state"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Custom Drill
            </Button>
          </div>
        ) : showCreate ? (
          /* ─── Quick Create Form ─── */
          <div className="p-4 space-y-3">
            <p className="text-xs text-slate-500">
              Create a private <span className="font-bold text-slate-700">{sectionLabel}</span> drill
              {selectedTopic && <> with tag <span className="font-bold text-amber-600">{selectedTopic}</span></>}.
            </p>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Name *</label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. 3-on-2 Fast Break"
                className="h-9 text-sm" data-testid="quick-drill-name" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Description (optional)</label>
              <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Brief instructions..."
                className="h-9 text-sm" data-testid="quick-drill-desc" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Duration (min)</label>
              <Input type="number" min={1} max={120} value={newDuration}
                onChange={e => setNewDuration(parseInt(e.target.value) || 10)}
                className="h-9 text-sm w-24" data-testid="quick-drill-duration" />
            </div>
            <p className="text-[10px] text-slate-400">This drill will be private. Add meta tags later in Drill Library to make it public.</p>
            <div className="flex gap-2 pt-1">
              <Button onClick={handleCreate} disabled={creating || !newName.trim()}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider text-sm"
                data-testid="quick-drill-submit">
                {creating ? 'Creating...' : 'Create & Add'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)}
                className="font-bold uppercase tracking-wider text-sm">
                Back
              </Button>
            </div>
          </div>
        ) : step === 1 ? (
          /* ─── STEP 1: Select Topic ─── */
          <>
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-700">What do you want to work on?</p>
              <p className="text-xs text-slate-500 mt-0.5">Select one focus area to find relevant drills</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <p className="text-xs text-slate-400 py-8 text-center">Loading...</p>
              ) : (
                FILTER_TAG_GROUPS.map(group => (
                  <div key={group.label}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">{group.label}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {group.tags.map(tag => {
                        const count = topicCounts[tag] || 0;
                        const isSelected = selectedTopic === tag;
                        const isRecommended = focusTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => setSelectedTopic(isSelected ? null : tag)}
                            disabled={count === 0}
                            className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                              isSelected 
                                ? 'border-[#13314b] bg-[#13314b]/5 ring-2 ring-[#13314b]/25' 
                                : count === 0
                                  ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                            }`}
                            data-testid={`topic-${tag.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <span className={`text-xs font-semibold leading-tight ${isSelected ? 'text-[#13314b]' : 'text-slate-700'}`}>
                                {tag}
                              </span>
                              {isRecommended && (
                                <span className="text-[8px] bg-amber-100 text-amber-700 px-1 rounded font-bold shrink-0">REC</span>
                              )}
                            </div>
                            <p className={`text-[10px] mt-0.5 ${isSelected ? 'text-[#13314b]' : 'text-slate-400'}`}>
                              {count} drill{count !== 1 ? 's' : ''}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Step 1 Footer */}
            <div className="px-4 py-3 border-t border-slate-200 bg-white">
              <Button 
                onClick={handleAcceptTopic}
                disabled={!selectedTopic}
                className="w-full bg-[#13314b] hover:bg-[#0e2638] text-white font-bold uppercase tracking-wider text-sm h-11"
                data-testid="accept-topic-btn"
              >
                {selectedTopic ? `Continue with "${selectedTopic}"` : 'Select a Topic'}
              </Button>
            </div>
          </>
        ) : (
          /* ─── STEP 2: Select Drill ─── */
          <>
            {/* Selected Topic + Filters */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 space-y-3">
              {/* Selected Topic Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Topic:</span>
                  <span className="px-2 py-0.5 bg-[#13314b]/10 text-[#13314b] rounded-full text-xs font-semibold">
                    {selectedTopic}
                  </span>
                </div>
                <button 
                  onClick={handleBack}
                  className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                  data-testid="change-topic-btn"
                >
                  Change
                </button>
              </div>

              {/* Difficulty Row */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Difficulty</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'beginner', label: 'Beginner', color: 'bg-green-100 text-green-700 border-green-300' },
                    { value: 'intermediate', label: 'Intermediate', color: 'bg-amber-100 text-amber-700 border-amber-300' },
                    { value: 'advanced', label: 'Advanced', color: 'bg-red-100 text-red-700 border-red-300' },
                  ].map(d => (
                    <button
                      key={d.value}
                      onClick={() => toggleFilter(difficultyFilters, setDifficultyFilters, d.value)}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full border transition-all ${
                        difficultyFilters.includes(d.value) 
                          ? d.color + ' ring-2 ring-offset-1 ring-slate-400' 
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}
                      data-testid={`filter-difficulty-${d.value}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ownership Row */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Show</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'mine', label: 'My Drills', icon: '👤' },
                    { value: 'favorites', label: 'Favorites', icon: '⭐' },
                    { value: 'public', label: 'Public', icon: '🌐' },
                  ].map(o => (
                    <button
                      key={o.value}
                      onClick={() => toggleFilter(ownershipFilters, setOwnershipFilters, o.value)}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full border transition-all flex items-center gap-1 ${
                        ownershipFilters.includes(o.value) 
                          ? 'bg-[#13314b]/10 text-[#13314b] border-[#13314b]/40 ring-2 ring-offset-1 ring-[#13314b]/50' 
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}
                      data-testid={`filter-ownership-${o.value}`}
                    >
                      <span className="text-[10px]">{o.icon}</span> {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Results count + Clear */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500">
                  {filteredDrills.length} drill{filteredDrills.length !== 1 ? 's' : ''} available
                </p>
                {hasActiveFilters && (
                  <button 
                    onClick={clearFilters}
                    className="text-[10px] font-semibold text-slate-500 hover:text-red-600"
                    data-testid="clear-filters"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {/* Drill List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5 min-h-0">
              {loading ? (
                <p className="text-xs text-slate-400 py-8 text-center">Loading drills...</p>
              ) : filteredDrills.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-xs text-slate-400 mb-3">No drills found for this topic</p>
                  <Button size="sm" onClick={() => setShowCreate(true)}
                    className="bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider text-xs gap-1"
                    data-testid="create-custom-drill-empty">
                    <Plus className="w-3 h-3" /> Create Custom Drill
                  </Button>
                </div>
              ) : (
                filteredDrills.map(d => {
                  const isFavorite = favorites.includes(d.drill_id);
                  const isMine = d.created_by === user?.user_id;
                  const isSelected = selectedDrill?.drill_id === d.drill_id;
                  
                  return (
                    <button 
                      key={d.drill_id} 
                      onClick={() => setSelectedDrill(isSelected ? null : d)}
                      className={`w-full text-left p-2.5 rounded-lg border-2 transition-all group ${
                        isSelected 
                          ? 'border-[#13314b] bg-[#13314b]/5 ring-2 ring-[#13314b]/25' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`pick-drill-${d.drill_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-[#13314b]' : 'text-slate-800'}`}>{d.name}</p>
                            {isMine && <span className="text-[9px] bg-purple-100 text-purple-600 px-1 rounded">Mine</span>}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                            <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{d.duration_minutes}m</span>
                            <span className="capitalize">{d.difficulty}</span>
                          </div>
                        </div>
                        {/* Favorite Button */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(e) => toggleFavorite(d.drill_id, e)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleFavorite(d.drill_id, e); }}
                          className={`p-1.5 rounded-full transition-all cursor-pointer ${
                            isFavorite 
                              ? 'text-amber-500 bg-amber-50 hover:bg-amber-100' 
                              : 'text-slate-300 hover:text-amber-400 hover:bg-slate-100 opacity-0 group-hover:opacity-100'
                          }`}
                          data-testid={`favorite-${d.drill_id}`}
                        >
                          <svg className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </div>
                      </div>
                      {d.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1.5">
                          {d.tags.slice(0, 4).map((t, i) => (
                            <span 
                              key={i} 
                              className={`text-[9px] px-1 py-0 rounded ${
                                t === selectedTopic 
                                  ? 'bg-[#13314b]/20 text-[#13314b] font-bold' 
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {t}
                            </span>
                          ))}
                          {d.tags.length > 4 && (
                            <span className="text-[9px] text-slate-400">+{d.tags.length - 4}</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {/* Step 2 Footer */}
            <div className="px-4 py-3 border-t border-slate-200 bg-white space-y-2">
              <Button 
                onClick={handleAddDrill}
                disabled={!selectedDrill}
                className="w-full bg-[#13314b] hover:bg-[#0e2638] text-white font-bold uppercase tracking-wider text-sm h-11"
                data-testid="add-drill-btn"
              >
                {selectedDrill ? `Add "${selectedDrill.name}"` : 'Select a Drill'}
              </Button>
              {filteredDrills.length > 0 && (
                <button 
                  onClick={() => setShowCreate(true)}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 py-2 transition-colors"
                  data-testid="create-custom-drill-bottom"
                >
                  <Plus className="w-3 h-3" /> Create Custom Drill
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
